import React, { useState } from "react";
import { api, apiError } from "../lib/api";
import { Badge } from "../components/ui";

const ENTITIES = [
  { key: "organisations", label: "Organisations" },
  { key: "contacts", label: "Contacts" },
  { key: "contrats", label: "Contrats" },
  { key: "devis", label: "Devis" },
];

export default function ImportPage() {
  const [entity, setEntity] = useState("organisations");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [result, setResult] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const doPreview = async () => {
    if (!file) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("entity", entity);
      fd.append("file", file);
      const { data } = await api.post("/import/preview", fd);
      setPreview(data);
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doCommit = async () => {
    if (!file) return;
    setBusy(true);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append("entity", entity);
      fd.append("file", file);
      const { data } = await api.post("/import/commit", fd);
      setResult(data);
      setPreview(null);
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadSample = async () => {
    const res = await api.get(`/import/exemple/${entity}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `exemple_${entity}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="import-page" className="max-w-4xl space-y-4">
      <h1 className="text-2xl font-extrabold text-green">Import CSV</h1>
      <div className="nx-card space-y-4 p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="nx-label">Type de données</label>
            <select data-testid="import-entity-select" className="nx-input !w-48" value={entity} onChange={(e) => { setEntity(e.target.value); setPreview(null); setResult(null); }}>
              {ENTITIES.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
            </select>
          </div>
          <div className="flex-1">
            <label className="nx-label">Fichier CSV</label>
            <input data-testid="import-file-input" type="file" accept=".csv,text/csv" className="nx-input" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); }} />
          </div>
          <button data-testid="import-preview-btn" className="nx-btn-primary" disabled={!file || busy} onClick={doPreview}>
            {busy ? "Analyse…" : "Analyser"}
          </button>
          <button data-testid="import-sample-btn" className="nx-btn-ghost" onClick={downloadSample}>Jeu d'exemple</button>
        </div>
        <p className="text-xs text-muted">
          Détection automatique de l'encodage et du séparateur. Les colonnes sont mappées automatiquement ; les organisations
          manquantes sont créées et les statuts normalisés.
        </p>
      </div>

      {err && <p data-testid="import-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-danger">{err}</p>}

      {preview && (
        <div className="nx-card space-y-3 p-5" data-testid="import-preview">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-green text-white">{preview.nb_lignes} ligne(s)</Badge>
            <Badge>Séparateur « {preview.separateur} »</Badge>
            {preview.colonnes_non_reconnues.length > 0 && (
              <Badge className="bg-amber-50 text-amber" testId="import-unrecognized">
                Colonnes non reconnues : {preview.colonnes_non_reconnues.join(", ")}
              </Badge>
            )}
          </div>
          <div>
            <h3 className="mb-1 text-sm font-bold text-green">Mapping des colonnes</h3>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(preview.mapping).map(([src, dst]: any) => (
                <Badge key={src} className="bg-lime-soft text-green">{src} → {dst}</Badge>
              ))}
            </div>
          </div>
          {preview.nb_valeurs_inconnues > 0 && (
            <div data-testid="import-ref-warnings" className="rounded-lg bg-amber-50 p-3 text-sm text-amber">
              <p className="mb-1 font-bold">
                {preview.nb_valeurs_inconnues} valeur(s) de référentiel non reconnue(s) — elles seront importées à vide :
              </p>
              <ul className="max-h-40 list-disc space-y-0.5 overflow-y-auto pl-5">
                {preview.anomalies_referentiel.map((a: any, i: number) => (
                  <li key={i}>Ligne {a.ligne} — {a.champ} : « {a.valeur} »</li>
                ))}
              </ul>
              <p className="mt-1 text-xs">
                Ajoutez ces valeurs dans Paramétrage › Listes de valeurs avant l'import pour les conserver.
              </p>
            </div>
          )}
          <div className="overflow-x-auto">
            <h3 className="mb-1 text-sm font-bold text-green">Aperçu (10 premières lignes)</h3>
            <table className="nx-table">
              <thead><tr>{preview.colonnes.map((c: string) => <th key={c}>{c}</th>)}</tr></thead>
              <tbody>
                {preview.apercu.map((row: any, i: number) => (
                  <tr key={i}>{preview.colonnes.map((c: string) => <td key={c} className="max-w-[160px] truncate">{row[c]}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end">
            <button data-testid="import-commit-btn" className="nx-btn-lime" disabled={busy} onClick={doCommit}>
              {busy ? "Import…" : `Valider l'import (${preview.nb_lignes} ${preview.nb_lignes > 1 ? "lignes" : "ligne"})`}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="nx-card p-5" data-testid="import-result">
          <p className="text-base font-bold text-green">✓ {result.crees} enregistrement(s) créé(s)</p>
          {result.erreurs.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm text-danger">
              {result.erreurs.map((e: any, i: number) => (
                <li key={i}>Ligne {e.ligne} : {e.erreur}</li>
              ))}
            </ul>
          )}
          {result.nb_valeurs_inconnues > 0 && (
            <div data-testid="import-result-ref-warnings" className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber">
              <p className="mb-1 font-bold">
                {result.nb_valeurs_inconnues} valeur(s) de référentiel non reconnue(s) laissée(s) vide(s) :
              </p>
              <ul className="max-h-40 list-disc space-y-0.5 overflow-y-auto pl-5">
                {result.valeurs_inconnues.map((a: any, i: number) => (
                  <li key={i}>Ligne {a.ligne} — {a.champ} : « {a.valeur} »</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
